import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import moment from 'moment';
import { EPresciption, EPresciptionINFOEXECUTIONInnerEXECUTANT, EPresciptionINFOEXECUTIONInnerEXECUTIONInner, EPresciptionINFOEXECUTIONInnerEXECUTIONInnerLIGEXECUTIONInner, EPresciptionINFOEXECUTIONInnerPATIENT, EPresciptionINFOPRESCRIPTIONPATIENT } from 'src/app/services/ordonnance-numerique/connecteur-models';
import { getMotifLibelleByCode, getSousMotifLibelleByCode } from './eprescription-format-utils';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';
import { ReferentielEnum } from 'src/app/utils/enums/referentiel-enum';
import { BcbService } from 'src/app/services/bcb/bcb.service';
import { RechercheBcbProduit } from 'src/app/utils/interfaces/new/recherche-bcb-produit.interface';
import { AnnuaireProfessionnelsSanteService } from 'src/app/services/annuaire-professionnels-sante/annuaire-professionnels-sante.service';
import { InfoAnnuaire } from 'src/app/utils/interfaces/new/info-annuaire.interface';
import { environment } from 'src/environments/environment';

interface PrescriptionPatient {
  nom?: string;
  nomUsage?: string;
  prenom?: string;
  numINS?: string;
  numNIR?: string;
  cleNIR?: string;
  numNIROD?: string;
  cleNIROD?: string;
  dateNai?: string;
  codeCentre?: string;
  codeOrganisme?: string;
  grandRegime?: string;
  rang?: number;
  poids?: number;
  taille?: number;
}

@Component({
  selector: 'app-display-eprescription',
  templateUrl: './display-eprescription.fragment.html',
  styleUrls: [
    './display-eprescription.fragment.scss',
    '../modal.fragment.scss'
  ]
})
export class DisplayEprescriptionFragment implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public prescription: EPresciption;
  public prescPatient: PrescriptionPatient = {};
  public loading: boolean;
  private _specialitesMap: Map<number, string> = new Map<number, string>();

  @Output() public closeEmitter = new EventEmitter();

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  constructor(
    private _domSanitize: DomSanitizer,
    private _ref: ReferentielService,
    private _bcbSrv: BcbService,
    private _annSrv: AnnuaireProfessionnelsSanteService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this._initSpecialitesMap();
    await this._formatPrescription();
    this.loading = false;
  }

  private async _initSpecialitesMap(): Promise<void> {
    const specialitesSesamTmp = await (await this._ref.findRefListTableHypermed(ReferentielEnum.SpecialiteSesamVitale))
      .map((qualite) => { return { id: qualite.id, libelle: qualite.libelle } });
    for (const spe of specialitesSesamTmp) {
      this._specialitesMap.set(Number(spe.id), spe.libelle);
    }
  }

  private async _formatPrescription(): Promise<void> {
    // this.prescription.INFOPRESCRIPTION.PRESCRIPTION.Consentement = true;

    if (this.prescription?.INFOPRESCRIPTION?.PRESCRIPTION?.Type) {
      this.prescription.INFOPRESCRIPTION.PRESCRIPTION.Type = this._setPrescriptionType(this.prescription.INFOPRESCRIPTION.PRESCRIPTION.Type);
    }

    if (this.prescription?.INFOPRESCRIPTION?.PRESCRIPTION?.SousType) {
      this.prescription.INFOPRESCRIPTION.PRESCRIPTION.SousType = this._setPrescriptionSousType(this.prescription.INFOPRESCRIPTION.PRESCRIPTION.SousType);
    }

    if (this.prescription?.INFOPRESCRIPTION?.PATIENT) {
      this._setPatient(this.prescription.INFOPRESCRIPTION.PATIENT);
    }

    if (this.prescription?.INFOPRESCRIPTION?.PRESCRIPTION?.Date) {
      this.prescription.INFOPRESCRIPTION.PRESCRIPTION.Date = this._setDateFormat(this.prescription.INFOPRESCRIPTION.PRESCRIPTION.Date);
    }

    if (this.prescription?.INFOPRESCRIPTION?.PRESCRIPTEUR) {
      this.prescription.INFOPRESCRIPTION.PRESCRIPTEUR = await this._setIdentifiantEtType(this.prescription.INFOPRESCRIPTION.PRESCRIPTEUR, true);
    }

    const execMap: Map<number, Array<EPresciptionINFOEXECUTIONInnerEXECUTIONInnerLIGEXECUTIONInner>> = new Map<number, Array<EPresciptionINFOEXECUTIONInnerEXECUTIONInnerLIGEXECUTIONInner>>();

    if (this.prescription?.INFOEXECUTION && this.prescription.INFOEXECUTION.length > 0) {
      for (const execution of this.prescription.INFOEXECUTION) {

        if (execution.PATIENT) {
          this._setPatient(execution.PATIENT);
        }

        if (execution.PATIENT?.DateNai) {
          execution.PATIENT.DateNai = this._setDateFormat(execution.PATIENT.DateNai);
        }

        if (execution.EXECUTION && execution.EXECUTION.length > 0) {
          for (const executionInter of execution.EXECUTION) {
            if (executionInter.Date) {
              executionInter.Date = this._setDateFormat(executionInter.Date);
            }

            if (executionInter.FACTURE?.FACTUREENVELOPPETECHNIQUE?.DateCreationLot) {
              executionInter.FACTURE.FACTUREENVELOPPETECHNIQUE.DateCreationLot = this._setDateFormat(executionInter.FACTURE.FACTUREENVELOPPETECHNIQUE.DateCreationLot);
            }

            if (executionInter.LIGEXECUTION && executionInter.LIGEXECUTION.length > 0) {
              for (const ligneExecution of executionInter.LIGEXECUTION) {
                if (ligneExecution.LIGBIO) {
                  (ligneExecution as any).Type = 'Analyse de biologie médicale';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGBIO);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGBIO.Id;
                } else if (ligneExecution.LIGDISPOSITIFMEDICAL) {
                  (ligneExecution as any).Type = 'Dispositif médical';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGDISPOSITIFMEDICAL);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGDISPOSITIFMEDICAL.Id;
                } else if (ligneExecution.LIGINFIRMIER) {
                  (ligneExecution as any).Type = 'Infirmier';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGINFIRMIER);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGINFIRMIER.Id;
                } else if (ligneExecution.LIGKINE) {
                  (ligneExecution as any).Type = 'Kinésithérapie';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGKINE);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGKINE.Id;
                } else if (ligneExecution.LIGMEDICAMENTEUX) {
                  (ligneExecution as any).Type = 'Produit de santé';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGMEDICAMENTEUX);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGMEDICAMENTEUX.Id;
                } else if (ligneExecution.LIGORTHOPHONISTE) {
                  (ligneExecution as any).Type = 'Orthophoniste';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGORTHOPHONISTE);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGORTHOPHONISTE.Id;
                } else if (ligneExecution.LIGORTHOPTISTE) {
                  (ligneExecution as any).Type = 'Orthoptiste';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGORTHOPTISTE);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGORTHOPTISTE.Id;
                } else if (ligneExecution.LIGPEDICURE) {
                  (ligneExecution as any).Type = 'Pédicure';
                  (ligneExecution as any).LigneCalculee = await this._calcLigneExec(ligneExecution.LIGPEDICURE);
                  (ligneExecution as any).NumLigne = ligneExecution.LIGPEDICURE.Id;
                }

                (ligneExecution as any).Date = executionInter.Date;
                if (executionInter.FACTURE) {
                  (ligneExecution as any).FACTURE = executionInter.FACTURE;
                }

                (ligneExecution as any).executant = await this._setIdentifiantEtType(execution.EXECUTANT);

                if (execMap.has((ligneExecution as any).NumLigne)) {
                  execMap.get((ligneExecution as any).NumLigne).push(ligneExecution);
                } else {
                  execMap.set((ligneExecution as any).NumLigne, [ligneExecution]);
                }
              }
            }
          }
        }
      }
    }
    console.log('execMap : ', execMap);

    const modifMap: Map<number, Array<EPresciptionINFOEXECUTIONInnerEXECUTIONInnerLIGEXECUTIONInner>> = new Map<number, Array<EPresciptionINFOEXECUTIONInnerEXECUTIONInnerLIGEXECUTIONInner>>();

    if (this.prescription?.INFOMODIFICATION && this.prescription.INFOMODIFICATION.length > 0) {
      for (const modification of this.prescription.INFOMODIFICATION) {

        if (modification.MODIFICATION && modification.MODIFICATION.length > 0) {
          for (const modificationInter of modification.MODIFICATION) {
            if (modificationInter.Date) {
              modificationInter.Date = this._setDateFormat(modificationInter.Date);
            }

            if (modificationInter.LIGMODIFICATION && modificationInter.LIGMODIFICATION.length > 0) {
              for (const ligneModification of modificationInter.LIGMODIFICATION) {
                if (ligneModification.LIGBIO) {
                  (ligneModification as any).Type = 'Analyse de biologie médicale';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGBIO);
                  (ligneModification as any).NumLigne = ligneModification.LIGBIO.Id;
                } else if (ligneModification.LIGDISPOSITIFMEDICAL) {
                  (ligneModification as any).Type = 'Dispositif médical';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGDISPOSITIFMEDICAL);
                  (ligneModification as any).NumLigne = ligneModification.LIGDISPOSITIFMEDICAL.Id;
                } else if (ligneModification.LIGINFIRMIER) {
                  (ligneModification as any).Type = 'Infirmier';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGINFIRMIER);
                  (ligneModification as any).NumLigne = ligneModification.LIGINFIRMIER.Id;
                } else if (ligneModification.LIGKINE) {
                  (ligneModification as any).Type = 'Kinésithérapie';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGKINE);
                  (ligneModification as any).NumLigne = ligneModification.LIGKINE.Id;
                } else if (ligneModification.LIGMEDICAMENTEUX) {
                  (ligneModification as any).Type = 'Produit de santé';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGMEDICAMENTEUX);
                  (ligneModification as any).NumLigne = ligneModification.LIGMEDICAMENTEUX.Id;
                } else if (ligneModification.LIGORTHOPHONISTE) {
                  (ligneModification as any).Type = 'Orthophoniste';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGORTHOPHONISTE);
                  (ligneModification as any).NumLigne = ligneModification.LIGORTHOPHONISTE.Id;
                } else if (ligneModification.LIGORTHOPTISTE) {
                  (ligneModification as any).Type = 'Orthoptiste';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGORTHOPTISTE);
                  (ligneModification as any).NumLigne = ligneModification.LIGORTHOPTISTE.Id;
                } else if (ligneModification.LIGPEDICURE) {
                  (ligneModification as any).Type = 'Pédicure';
                  (ligneModification as any).LigneCalculee = await this._calcLigneExec(ligneModification.LIGPEDICURE);
                  (ligneModification as any).NumLigne = ligneModification.LIGPEDICURE.Id;
                }

                (ligneModification as any).Date = modificationInter.Date;
                (ligneModification as any).AccordPrescripteur = ligneModification.AccordPrescripteur ? 'Oui' : 'Non';
                (ligneModification as any).CodeMotif = this._setMotifLibelle(ligneModification.CodeMotif);
                (ligneModification as any).CodeSousMotif = this._setSousMotifLibelle(ligneModification.CodeSousMotif);

                (ligneModification as any).executant = await this._setIdentifiantEtType(modification.EXECUTANT);

                if (modifMap.has((ligneModification as any).NumLigne)) {
                  modifMap.get((ligneModification as any).NumLigne).push(ligneModification);
                } else {
                  modifMap.set((ligneModification as any).NumLigne, [ligneModification]);
                }
              }
            }
          }
        }
      }
    }
    console.log('modifMap : ', modifMap);

    if (this.prescription?.INFOPRESCRIPTION?.PRESCRIPTION?.CONTENUPRESCRIPTION) {
      const contenuKeys = Object.keys(this.prescription.INFOPRESCRIPTION.PRESCRIPTION.CONTENUPRESCRIPTION);
      for (const contenuKey of contenuKeys) {
        if (this.prescription.INFOPRESCRIPTION.PRESCRIPTION.CONTENUPRESCRIPTION[contenuKey]) {

          if (!(this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes) {
            (this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes = [];
          }

          for (const contenu of this.prescription.INFOPRESCRIPTION.PRESCRIPTION.CONTENUPRESCRIPTION[contenuKey]) {
            let execution;
            if (execMap.has(contenu.NumLigne)) {
              execution = execMap.get(contenu.NumLigne);
              execMap.delete(contenu.NumLigne);
            }

            let modification;
            if (modifMap.has(contenu.NumLigne)) {
              modification = modifMap.get(contenu.NumLigne);
              modifMap.delete(contenu.NumLigne);
            }

            (this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes.push({
              NumLigne: contenu.NumLigne,
              informationsLigne: await this._calcLigneExec(contenu),
              executionLignes: execution,
              modificationLignes: modification,
            });
          }
        }
      }

      const indexModifsRestantes = modifMap.keys();
      if (indexModifsRestantes) {
        for (const modifManuscIdx of indexModifsRestantes) {
          if (modifMap.has(modifManuscIdx)) {
            const newModifLine = modifMap.get(modifManuscIdx);
            let newExecLine;
            if (execMap.has(modifManuscIdx)) {
              newExecLine = execMap.get(modifManuscIdx);
            }

            (this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes.push({
              NumLigne: modifManuscIdx,
              informationsLigne: undefined,
              executionLignes: newExecLine,
              modificationLignes: newModifLine,
            });
          }
        }
      }
      console.log('LIGNES : ', (this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes);
    }
    // On retrie les lignes par NumLigne
    (this.prescription.INFOPRESCRIPTION.PRESCRIPTION as any).Lignes.sort((a, b) => {
      return a.NumLigne - b.NumLigne;
    });
  }

  private async _setIdentifiantEtType(executant: EPresciptionINFOEXECUTIONInnerEXECUTANT, isPrescripteur = false): Promise<any> {
    const rtd = {};
    if (executant.Identifiant) {
      rtd['Identifiant'] = executant.Identifiant;
      rtd['Type'] = 'N° AM';
    }else if (executant.STS?.Identifiant) {
      rtd['Identifiant'] = executant.STS?.Identifiant;
      rtd['Type'] = 'N° FINESS';
    } else {
      rtd['Identifiant'] = ' -- ';
      rtd['Type'] = 'Identifiant';
    }

    if (executant.IdentifiantRPPSSecondaire) {
      rtd['IdentifiantRemplacant'] = executant.IdentifiantRPPSSecondaire;
      rtd['TypeRemplacant'] = 'RPPS remplaçant';
    }

    let specialite;
    if (executant.Specialite && !Number.isNaN(Number(executant.Specialite)) && this._specialitesMap.has(Number(executant.Specialite))) {
      specialite = this._specialitesMap.get(Number(executant.Specialite));
    }

    rtd['Specialite'] = specialite
    rtd['Nom'] = executant.Nom || undefined;
    rtd['Prenom'] = executant.Prenom || undefined;
    rtd['IdentifiantRPPS'] = executant.IdentifiantRPPS || undefined;

    if (!isPrescripteur && executant.IdentifiantRPPS) {
      const infoAnn = await this._getExecutantByRpps(executant.IdentifiantRPPS);
      if (infoAnn) {
        let adresse = '';

        if (infoAnn.adresse) {
          adresse += infoAnn.adresse;
        }
        if (infoAnn.codePostal) {
          if (adresse !== '') {
            adresse += '<br>';
          }

          adresse += infoAnn.codePostal;
        }
        if (infoAnn.commune) {
          adresse += ` ${infoAnn.commune}`;
        }

        rtd['RaisSoc'] = infoAnn.raisonSociale || undefined;
        rtd['Adresse'] = adresse && adresse !== '' ? adresse : undefined;
      }
    }

    return rtd;
  }

  private _setDateFormat(date: string): string {
    return moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
  }

  private async _calcLigneExec(ligne: any): Promise<any> {
    const _convertTypeEltPrescrit = (typeEltPrescrit: string): string => {
      switch (typeEltPrescrit) {
        case "01":
          return 'Produit'
          // return 'Produit en CIP';
        case "02":
          return 'Produit'
          // return 'Produit en DCI';
        case "04":
          return 'Préparation magistrale';
        default:
          return 'Type non renseigné';
      }
    };

    const keys = Object.keys(ligne);
    const rtd = {};
    for (const key of keys) {
      if (ligne[key]) {
        if (key === 'TypeEltPrescrit') {
          ligne[key] = _convertTypeEltPrescrit(ligne[key]);
        }
        if (key === 'CIP' && ligne[key]) {
          const produit = await this._getProduitByCip(ligne[key]);
          if (produit) {
            // ligne[key] = `${ligne[key]} (${produit.libelle})`;
            ligne[key] = `${produit.libelle}`;
          }
        }

        if (typeof ligne[key] === 'string') {
          rtd[key] = this._sanitizeValue(ligne[key]);
        } else if (typeof ligne[key] === 'boolean') {
          rtd[key] = this._sanitizeValue(ligne[key] === true ? 'Oui' : 'Non');
        } else if (typeof ligne[key] === 'number') {
          rtd[key] = this._sanitizeValue(Number(ligne[key]));
        }
      }
    }
    return rtd;
  }

  private _setPrescriptionType(type: string): string {
    switch (type) {
      case 'PRODUITS_SANTE':
        return 'Produits de santé';
      case 'BIO':
        return 'Analyse de biologie médicale';
      case 'KINE':
        return 'Kinésithérapeute';
      case 'INFIRMIER':
        return 'Infirmier';
      case 'PEDICURE':
        return 'Pédicure';
      case 'ORTHOPHONISTE':
        return 'Orthophoniste';
      case 'ORTHOPTISTE':
        return 'Orthoptiste';
      default:
        return ' -- ';
    }
  }

  private _setPrescriptionSousType(sousType: string): string {
    switch (sousType) {
      case 'ALD':
        return 'ALD';
      case 'MEX':
        return 'Médicament d\'exception';
      case 'BZN':
        return 'Bizone';
      case 'OSC':
        return 'Produits sécurisés';
      default:
        return ' -- ';
    }
  }

  private _setMotifLibelle(code: number): string {
    return getMotifLibelleByCode(code);
  }

  private _setSousMotifLibelle(code: number): string {
    return getSousMotifLibelleByCode(code);
  }

  private _setPatient(patient: EPresciptionINFOPRESCRIPTIONPATIENT | EPresciptionINFOEXECUTIONInnerPATIENT): void {
    if (!this.prescPatient.nom && (patient as any)?.Nom) {
      this.prescPatient.nom = (patient as any).Nom;
    }

    if (!this.prescPatient.nomUsage && (patient as any)?.NomUsage) {
      this.prescPatient.nomUsage = (patient as any).NomUsage;
    }

    if (!this.prescPatient.prenom && (patient as any)?.Prenom) {
      this.prescPatient.prenom = (patient as any).Prenom;
    }

    if (!this.prescPatient.numINS && (patient as any)?.NumINS) {
      this.prescPatient.numINS = (patient as any).NumINS;
    }

    if (!this.prescPatient.numNIR && (patient as any)?.NumNIR) {
      this.prescPatient.numNIR = (patient as any).NumNIR;
    }

    if (!this.prescPatient.cleNIR && (patient as any)?.CleNIR) {
      this.prescPatient.cleNIR = (patient as any).CleNIR;
    }

    if (!this.prescPatient.numNIROD && (patient as any)?.NumNIROD) {
      this.prescPatient.numNIROD = (patient as any).NumNIROD;
    }

    if (!this.prescPatient.cleNIROD && (patient as any)?.CleNIROD) {
      this.prescPatient.cleNIROD = (patient as any).CleNIROD;
    }

    if (!this.prescPatient.dateNai && (patient as any)?.DateNai) {
      this.prescPatient.dateNai = this._setDateFormat((patient as any).DateNai);
    }

    if (!this.prescPatient.codeCentre && (patient as any)?.CodeCentre) {
      this.prescPatient.codeCentre = (patient as any).CodeCentre;
    }

    if (!this.prescPatient.codeOrganisme && (patient as any)?.CodeOrganisme) {
      this.prescPatient.codeOrganisme = (patient as any).CodeOrganisme;
    }

    if (!this.prescPatient.grandRegime && (patient as any)?.GrandRegime) {
      this.prescPatient.grandRegime = (patient as any).GrandRegime;
    }

    if (!this.prescPatient.rang && (patient as any)?.Rang) {
      this.prescPatient.rang = (patient as any).Rang;
    }

    if (!this.prescPatient.poids && (patient as any)?.Poids) {
      this.prescPatient.poids = (patient as any).Poids;
    }

    if (!this.prescPatient.taille && (patient as any)?.Taille) {
      this.prescPatient.taille = (patient as any).Taille;
    }
  }

  private _sanitizeValue(str: string | number): SafeHtml {
    if (typeof str === 'number') {
      str = str.toString();
    }
    str = str.replace(/\\r/g, '<br>');
    return this._domSanitize.sanitize(SecurityContext.HTML, str);
  }

  public closeModal(): void {
    this.closeEmitter.emit();
  }

  private _produitsByCipMap: Map<string, RechercheBcbProduit> = new Map<string, RechercheBcbProduit>();
  private async _getProduitByCip(codeCip: string): Promise<RechercheBcbProduit> {
    try {
      if (this._produitsByCipMap.has(codeCip)) {
        return this._produitsByCipMap.get(codeCip);
      } else {
        const produit = await this._bcbSrv.rechercheProduitByCodeCip(codeCip);
        if (produit[0]) {
          this._produitsByCipMap.set(codeCip, produit[0]);
          return produit[0];
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  private _executantByRppsMap: Map<string, InfoAnnuaire | undefined> = new Map<string, InfoAnnuaire>();
  private async _getExecutantByRpps(rpps: string): Promise<InfoAnnuaire> {
    // rpps = '810000001254';
    try {
      if (this._executantByRppsMap.has(rpps)) {
        return this._executantByRppsMap.get(rpps);
      } else {

        if (rpps.length === 11) {
          rpps = "8" + rpps;
        }

        const infoAnn = await this._annSrv.findInfoByRpps(rpps);
        if (infoAnn) {
          this._executantByRppsMap.set(rpps, infoAnn);
          return infoAnn;
        } else {
          this._executantByRppsMap.set(rpps, undefined);
          return undefined;
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

}
